import { QueryStatus } from "@reduxjs/toolkit/query";
import { useWindowSize } from "hooks/useWindowSize";
import { AccountingMethodBanner } from "components/BooksSettings/AccountingMethodBanner";
import ConditionalToolTip from "components/design/conditionalToolTip";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Breadcrumb from "components/DesignSystem/Breadcrumb/Breadcrumb";
import { Button } from "components/DesignSystem/Button/Button";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import Card from "components/DesignSystem/Card/Card";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import Loader from "components/DesignSystem/Loader/loader";
import Modal from "components/DesignSystem/Modal/Modal";
import { SwitchField } from "components/DesignSystem/Switch/SwitchField";
import { FloatingChat } from "components/floatingChat/FloatingChat";
import { FormikForm } from "components/FormikForm/FormikForm";
import { BooksSettingLines } from "components/icons/Navbar/BooksSettingLines";
import { LockedUi } from "components/LockedUi/LockedUi";
import { ACCOUNTING_METHODS_OPTIONS } from "constants/bookkeeping";
import { FINANCIAL_CLOSING_CARD_TYPE } from "constants/chatType";
import { DISPLAY_DATE, MONTH_FORMAT, YYYY_MM_DD } from "constants/date";
import {
  CHECKLIST_ITEM_SECTION,
  CHECKLIST_ITEM_TYPE,
  CLOSING_STATUS,
  DOCUMENT_STATUS,
  FINANCIAL_CLOSING_V1_DATE,
} from "constants/financialClosing";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import dayjs from "dayjs";
import { FINANCIAL_CLOSING_ACTION_NAME } from "dictionaries";
import { Field, FieldProps, useFormikContext } from "formik";
import { financialClosingSchema } from "formValidations/financialClosingSchema";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { useModal } from "hooks/useModal";
import { usePageTitle } from "hooks/usePageTitle";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { SingleValue } from "react-select";
import CloseIcon from "static/images/Close.svg";
import DoubleGreenTick from "static/images/DoubleGreenTick.svg";
import { useUpdateBookkeepingSettingsMutation } from "store/apis/booksLockDate";
import {
  ChecklistItem,
  FinancialClosing,
  FinancialClosingDetail,
  useGetFinancialClosingQuery,
  useGetFinancialClosingSummaryQuery,
  useUpdateFinancialClosingCheckListMutation,
  useUpdateFinancialClosingMutation,
  useUpdateFinancialClosingStatusMutation,
} from "store/apis/financialClosing";
import { setChatCardAttachment } from "store/slices/financialClosing";
import { addMessageToQuote } from "store/slices/messegeIdToRemind";
import { FileObject } from "types/Models/fileObject";
import { BackendError } from "types/utils/error";
import { debounce } from "utils/debouncing";
import { ChangeRequestActions } from "./ChangeRequestActions";
import { ChangeRequestsAccordion } from "./ChangeRequestsAccordion";
import { Checklist, CheckListUpdatePayload } from "./CheckList";
import { Comments } from "./Comments";
import { FinalizeFinancialConfirmationModal } from "./FinalizeFinancialConfirmationModal";
import { FinancialStatus } from "./FinancialClosing";
import { ManageClosing } from "./ManageClosing";
import { OtherAttachmentSingle } from "./OtherAttachmentSingle";
import { SendForReviewConfrmationModal } from "./SendForReviewConfrmationModal";
import { SummaryCard } from "./Summary";
import { scrollToTheFirstClosingError } from "./utils/scrollToFirstClosingError";

export type FinancialClosingErrorState = {
  reconciliationAccounts?: string[];
  verifyCategorisation?: string[];
  reviewChanges?: string[];
  finalizeFinancials?: string[];
  otherFinancials?: string[];
  showError?: boolean;
};

export type ClosingFormValues = {
  closing_card_details: FinancialClosingDetail;
  checklist_items: ChecklistItem[];
  other_financials: FileObject[];
  lock_financials: boolean;
  accounting_lock_date: string;
};

// Create validation schema for the form
const useChangeStatusAllowed = ({
  status,
  is_service_team_user,
}: {
  status: FinancialClosingDetail["status"];
  is_service_team_user: FinancialClosingDetail["creator"]["is_service_team_user"];
}) => {
  const { isCpa, isCustomer } = useRoleBasedView();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.FINANCIALS
  );

  if (!hasWritePermission) {
    return false;
  }

  if (
    status === CLOSING_STATUS.COMPLETED ||
    status === CLOSING_STATUS.REQUEST_CHANGES ||
    status === CLOSING_STATUS.AMENDED
  ) {
    return false;
  }

  if (!is_service_team_user) {
    return true;
  }

  const ACTION_DISABLED = {
    [CLOSING_STATUS.IN_PROGRESS]: isCpa,
    [CLOSING_STATUS.REVIEW_PENDING]: isCustomer,
    [CLOSING_STATUS.REJECTED]: false,
    [CLOSING_STATUS.REQUEST_CHANGES]: false,
    [CLOSING_STATUS.AMENDED]: false,
    [CLOSING_STATUS.PENDING_INFO]: isCpa,
  };

  return ACTION_DISABLED[status];
};

const useEditAllowedForUser = ({
  status,
  is_service_team_user,
}: {
  status: FinancialClosingDetail["status"];
  is_service_team_user: FinancialClosingDetail["creator"]["is_service_team_user"];
}) => {
  const { isCpa } = useRoleBasedView();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.FINANCIALS
  );

  if (!hasWritePermission) {
    return false;
  }

  // pending info is still being worked on, so it stays editable like in progress
  const isOnGoing =
    status === CLOSING_STATUS.IN_PROGRESS ||
    status === CLOSING_STATUS.PENDING_INFO;

  if (isCpa && isOnGoing && is_service_team_user) {
    return true;
  }

  if (isOnGoing && !is_service_team_user) {
    return true;
  }

  return false;
};

const useGetApiStatus = (endpointName: string) => {
  const state = useAppSelector((state) => state.api.mutations);

  const updateFinancialClosing = Object.values(state).find(
    (e) => e?.endpointName === endpointName
  );

  const isLoading = updateFinancialClosing?.status === QueryStatus.pending;

  const isSuccess = updateFinancialClosing?.status === QueryStatus.fulfilled;

  return { isLoading, isSuccess };
};

const Details = ({
  isLoading,
  disabled,
}: {
  isLoading: boolean;
  disabled: boolean;
}) => {
  const { values, setFieldValue } = useFormikContext<{
    closing_card_details: FinancialClosingDetail;
    checklist_items: ChecklistItem[];
    other_financials: FinancialClosing["other_financials"];
    lock_financials: boolean;
    accounting_lock_date: string;
  }>();
  const { financialClosingId } = useParams<{ financialClosingId: string }>();
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.FINANCIALS
  );

  const defaultValue = ACCOUNTING_METHODS_OPTIONS.find(
    ({ value }) => value === values.closing_card_details.accounting_method
  );

  const [updateBooksSetting, { isLoading: isUpdating }] =
    useUpdateBookkeepingSettingsMutation();

  const [updateFinancialClosing] = useUpdateFinancialClosingMutation();

  const onDetailsChange = async (updateValues: {
    accounting_method?: FinancialClosingDetail["accounting_method"];
    start_date?: string;
    end_date?: string;
  }) => {
    try {
      await updateFinancialClosing({
        entityId: entityId,
        financialClosingId: financialClosingId,
        payload: {
          accounting_method: updateValues.accounting_method,
          start_date: updateValues.start_date,
          end_date: updateValues.end_date,
        },
      }).unwrap();
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  return (
    <Card.Root className="t-pb-5 t-rounded-lg sm:!t-px-3">
      <Card.Header>
        <Card.Title className="t-text-subtext">Details</Card.Title>
      </Card.Header>
      <Card.Body className="t-flex t-flex-col t-gap-4">
        <div className="t-flex t-gap-4 sm:t-gap-2">
          <div className="t-flex-1 t-min-w-0">
            <Field name="closing_card_details.start_date">
              {({ field }: FieldProps) => {
                return (
                  <DateInput
                    disabled={isLoading || disabled}
                    customSize="small"
                    {...field}
                    label="Start from"
                    placeholder="Select date"
                    block
                    showMonthYearPicker
                    dateFormat={MONTH_FORMAT}
                    required
                    onDateChange={debounce((date) => {
                      const start_date = dayjs(date).format(YYYY_MM_DD);
                      if (
                        start_date === values.closing_card_details.start_date
                      ) {
                        return;
                      }
                      onDetailsChange({
                        start_date,
                      });
                    })}
                  />
                );
              }}
            </Field>
          </div>
          <div className="t-flex-1 t-min-w-0">
            <Field name="closing_card_details.end_date">
              {({ field }: FieldProps) => {
                return (
                  <DateInput
                    disabled={isLoading || disabled}
                    customSize="small"
                    {...field}
                    label="To"
                    placeholder="Select date"
                    block
                    showMonthYearPicker
                    dateFormat={MONTH_FORMAT}
                    minDate={
                      values.closing_card_details.start_date
                        ? dayjs(values.closing_card_details.start_date).toDate()
                        : undefined
                    }
                    required
                    onDateChange={debounce((date) => {
                      const end_date = dayjs(date)
                        .endOf("M")
                        .format(YYYY_MM_DD);
                      if (end_date === values.closing_card_details.end_date) {
                        return;
                      }
                      onDetailsChange({
                        end_date,
                      });
                    })}
                  />
                );
              }}
            </Field>
          </div>
        </div>
        <Combobox
          isDisabled={isLoading || disabled}
          menuPortalTarget={document.body}
          size="small"
          label="Accounting method"
          name="closing_card_details.accounting_method"
          defaultValue={defaultValue}
          options={ACCOUNTING_METHODS_OPTIONS}
          isClearable={false}
          required
          onChange={debounce((option) => {
            const value = (option as SingleValue<OptionData>)?.value;

            if (value === values.closing_card_details.accounting_method) {
              return;
            }

            onDetailsChange({
              accounting_method:
                value as FinancialClosingDetail["accounting_method"],
            });
            setFieldValue("closing_card_details.accounting_method", value);
          })}
        />

        <AccountingMethodBanner
          selectedAccountingMethod={
            values.closing_card_details.accounting_method
          }
          entityId={entityId}
          hasWritePermission={hasWritePermission}
        />

        <InfoItem
          label="Status"
          dir="row"
          className="t-gap-[3.7rem] t-flex t-items-center sm:t-gap-12"
        >
          <FinancialStatus status={values.closing_card_details.status} />
        </InfoItem>
        <InfoItem
          label="Created by"
          dir="row"
          className="t-gap-8 t-flex t-items-center sm:t-gap-4 sm:t-items-start"
        >
          <div className="t-flex t-items-center t-gap-2 t-text-body-sm t-text-text-60 sm:t-flex-col sm:t-items-start sm:t-gap-0.5">
            <div>{values.closing_card_details.creator.name}</div>
            <div className="t-flex t-items-center t-justify-center t-rounded-full t-h-1 t-w-1 t-bg-[#D9D9D9] sm:t-hidden"></div>
            <div>
              {dayjs(values.closing_card_details.creator.created_at).format(
                DISPLAY_DATE
              )}
            </div>
          </div>
        </InfoItem>
        {values.closing_card_details?.preparer && (
          <InfoItem label="Preparer" dir="row" className="t-gap-8 sm:t-gap-6">
            <span className="t-text-subtext-sm t-text-text-100">
              {values.closing_card_details.preparer}
            </span>
          </InfoItem>
        )}
      </Card.Body>
    </Card.Root>
  );
};

export const FinancialClosingDetails = ({
  breadcrumbs,
  close,
}: {
  breadcrumbs: {
    name: string;
    link: string;
  }[];
  close: () => void;
}) => {
  usePageTitle("Financial closing details");
  const { financialClosingId } = useParams<{ financialClosingId: string }>();
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();
  const { uuid: groupId, entities } = useCurrentGroupContext();
  const { isMobile } = useWindowSize();
  const dispatch = useAppDispatch();
  const channelId = entities.find(
    ({ uuid }) => uuid === entityId
  )?.bookkeeping_channel;
  const checklistModal = useModal();

  const { isCpa, isCustomer } = useRoleBasedView();
  const history = useHistory();
  const { link } = useConstructInternalLink();
  const [validateOnMount, setValidateOnMount] = useState<boolean>(false);
  const chatSlider = useModal();

  const {
    data: financialClosingDetails,
    isLoading,
    isSuccess,
  } = useGetFinancialClosingQuery(
    {
      entityId,
      financialClosingId,
    },
    {
      skip: !financialClosingId || !entityId,
    }
  );

  const { data: summary } = useGetFinancialClosingSummaryQuery(
    {
      entityId,
      financialClosingId,
    },
    {
      skip: !financialClosingId || !entityId,
    }
  );

  const isCreatedByCustomer =
    !financialClosingDetails?.closing_card_details.creator.is_service_team_user;

  const {
    checklist_items = [],
    closing_card_details,
    other_financials = [],
  } = financialClosingDetails || {};

  const checkListItems = checklist_items.filter((c) => c.is_visible);

  const reviewMessageId = closing_card_details?.review_message_id;

  const updateFinancialClosingApiState = useGetApiStatus(
    "updateFinancialClosing"
  );
  const addOtherFinancialsApiState = useGetApiStatus("addOtherFinancials");
  const deleteOtherFinancialsApiState = useGetApiStatus(
    "deleteOtherFinancials"
  );
  const updateFinancialClosingCheckListApiState = useGetApiStatus(
    "updateFinancialClosingCheckList"
  );

  const isUpdating =
    updateFinancialClosingApiState.isLoading ||
    addOtherFinancialsApiState.isLoading ||
    deleteOtherFinancialsApiState.isLoading ||
    updateFinancialClosingCheckListApiState.isLoading;

  const success =
    updateFinancialClosingApiState.isSuccess &&
    addOtherFinancialsApiState.isSuccess &&
    deleteOtherFinancialsApiState.isSuccess &&
    updateFinancialClosingCheckListApiState.isSuccess;

  const [updateStatus, { isLoading: isSendingForReview }] =
    useUpdateFinancialClosingStatusMutation();

  const checkIsReviewed = () => {
    const finalizeFinancials = checkListItems.filter(
      ({ section }) => section === CHECKLIST_ITEM_SECTION.FINALIZE_FINANCIALS
    );

    const isReviewed = finalizeFinancials.every(
      (item) => item.resolved_by_admin || item.document_type === "NONE"
    );
    if (isReviewed) {
      return true;
    } else {
      alertToast({
        message: "Review All Finalize Financials",
      });
      return false;
    }
  };

  const [updateFinancialClosingCheckList] =
    useUpdateFinancialClosingCheckListMutation();

  const onCheckListChange = async ({
    resolved_by_admin,
    checklistItemId,
    file_url,
    file_data,
    document_type,
    document_id,
    delete_document,
  }: CheckListUpdatePayload) => {
    try {
      await updateFinancialClosingCheckList({
        entityId: entityId,
        financialClosingId: financialClosingId,
        checklistItemId: checklistItemId,
        payload: {
          admin_resolved: resolved_by_admin,
          file_data: file_data,
          file_url: file_url,
          document_type: document_type,
          delete_document: delete_document,
          document_id: document_id,
        },
      }).unwrap();
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  const onChangeStatus = async (status: FinancialClosingDetail["status"]) => {
    try {
      const isReviewed =
        status === CLOSING_STATUS.COMPLETED
          ? isCreatedByCustomer || checkIsReviewed()
          : true;

      if (isReviewed) {
        if (!channelId) {
          return alertToast({
            message: "No chat channel found for this entity",
          });
        }

        // If the status is REVIEW_PENDING, send the card to the chat
        if (status === CLOSING_STATUS.REVIEW_PENDING && closing_card_details) {
          chatSlider.open();
          dispatch(
            setChatCardAttachment({
              entityId: entityId,
              financialClosingId: financialClosingId,
              card_type: FINANCIAL_CLOSING_CARD_TYPE.REVIEW_FINANCIAL,
              start_date: closing_card_details?.start_date,
              end_date: closing_card_details?.end_date,
              status,
            })
          );
          return;
        }

        await updateStatus({
          entityId: entityId,
          financialClosingId: financialClosingId,
          payload: {
            status,
            accounting_lock_date: closing_card_details?.accounting_lock_date,
            ...(status === CLOSING_STATUS.COMPLETED && {
              send_completed_financials_email: true,
            }),
          },
        }).unwrap();

        successToast({
          message:
            status === CLOSING_STATUS.COMPLETED
              ? `Your ${closing_card_details?.period} financials have been finalised`
              : `Your financials for ${closing_card_details?.period} have been sent for review`,
          title:
            status === CLOSING_STATUS.COMPLETED
              ? "Financials finalised"
              : "Sent for review",
        });
        if (status === CLOSING_STATUS.COMPLETED) {
          history.push(
            `/books/financial-closing?entity=${entityId}&company=${groupId}`
          );
        }
      }
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  const dateUpdate = async (date: Date | null) => {
    try {
      await updateStatus({
        entityId: entityId,
        financialClosingId: financialClosingId,
        payload: {
          status: closing_card_details?.status,
          accounting_lock_date: date ? dayjs(date).format(YYYY_MM_DD) : null,
        },
      }).unwrap();
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  let isChangeStatusAllowed = useChangeStatusAllowed({
    status: closing_card_details?.status || CLOSING_STATUS.COMPLETED,
    is_service_team_user:
      closing_card_details?.creator.is_service_team_user || false,
  });

  const isReviewPendingAndCPA =
    closing_card_details?.status === CLOSING_STATUS.REVIEW_PENDING && isCpa;

  isChangeStatusAllowed = isReviewPendingAndCPA || isChangeStatusAllowed;

  const isEditAllowedForUser = useEditAllowedForUser({
    status: closing_card_details?.status || CLOSING_STATUS.COMPLETED,
    is_service_team_user:
      closing_card_details?.creator.is_service_team_user || false,
  });

  const finalizeFinancials = checkListItems.filter(
    ({ section }) => section === CHECKLIST_ITEM_SECTION.FINALIZE_FINANCIALS
  );

  const financialDocumentTypeUpdate = () => {
    const cashFlowFinancial = finalizeFinancials.find(
      (item) => item.type === CHECKLIST_ITEM_TYPE.REVIEW_CASHFLOW_STATEMENT
    );

    if (
      cashFlowFinancial &&
      cashFlowFinancial.document_type !== DOCUMENT_STATUS.UPLOAD
    ) {
      onCheckListChange({
        checklistItemId: cashFlowFinancial.uuid,
        document_type: DOCUMENT_STATUS.NONE,
        resolved_by_admin: false,
      });
    }
  };

  const reviewConformationModal = useModal();
  const reopenSendForReviewModal = useModal();
  const reviewfinalizeFinancialConfirmationModal = useModal();
  const onAmended = () => {
    history.push(link("/books/financial-closing"));
  };

  const onSendReviewConfirmation = async () => {
    try {
      reviewConformationModal.close();
      await onChangeStatus(
        closing_card_details?.status === CLOSING_STATUS.REVIEW_PENDING
          ? CLOSING_STATUS.COMPLETED
          : CLOSING_STATUS.REVIEW_PENDING
      );
      financialDocumentTypeUpdate();
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  if (isLoading || !isSuccess) {
    return (
      <Modal.Root open={true}>
        <Modal.Content size="fullscreen">
          <Loader />
        </Modal.Content>
      </Modal.Root>
    );
  }

  if (!closing_card_details) {
    return;
  }

  const actionName = FINANCIAL_CLOSING_ACTION_NAME[closing_card_details.status];

  const ctaTitle =
    (actionName === "Ready for review" && isCpa) ||
    (isReviewPendingAndCPA && !reviewMessageId)
      ? "Send for review"
      : isReviewPendingAndCPA
      ? "Send reminder"
      : actionName;
  const isNotOngoingState =
    closing_card_details?.status === CLOSING_STATUS.REVIEW_PENDING ||
    closing_card_details?.status === CLOSING_STATUS.COMPLETED;

  const onConfirmFinalizeFinancial = ({ state }: { state?: string }) => {
    onChangeStatus(
      state === CLOSING_STATUS.REVIEW_PENDING ||
        closing_card_details.status === CLOSING_STATUS.REVIEW_PENDING
        ? CLOSING_STATUS.COMPLETED
        : CLOSING_STATUS.REVIEW_PENDING
    );
    reviewfinalizeFinancialConfirmationModal.close();
  };

  const isOnGoing =
    closing_card_details.status === CLOSING_STATUS.IN_PROGRESS ||
    closing_card_details.status === CLOSING_STATUS.PENDING_INFO;
  const isChangesRequested =
    closing_card_details.status === CLOSING_STATUS.REQUEST_CHANGES;
  const isReadOnlyClosing =
    isChangesRequested ||
    closing_card_details.status === CLOSING_STATUS.AMENDED;
  const showResolveOrAmend = isChangesRequested && !isCustomer;
  const showStatusFooter =
    !isReadOnlyClosing &&
    closing_card_details.status !== CLOSING_STATUS.COMPLETED;
  const changeRequests = closing_card_details.change_requests ?? [];

  const sendReminder = () => {
    chatSlider.open();
    dispatch(addMessageToQuote(reviewMessageId!));
  };

  const handleFinalizeFinancialStatusChange = ({
    handleSendForReview,
    allowUserToChangeState,
  }: {
    handleSendForReview?: () => Promise<void>;
    allowUserToChangeState?: () => Promise<void>;
  }) => {
    if (isReviewPendingAndCPA && !reviewMessageId) {
      return reopenSendForReviewModal.open();
    }

    if (isReviewPendingAndCPA) {
      return sendReminder();
    }

    if (isCpa) {
      return handleSendForReview?.();
    }

    if (isOnGoing) {
      if (isCustomer) {
        return allowUserToChangeState?.();
      }

      const newStatus =
        closing_card_details.status === CLOSING_STATUS.REVIEW_PENDING
          ? CLOSING_STATUS.COMPLETED
          : CLOSING_STATUS.REVIEW_PENDING;

      return onChangeStatus(newStatus);
    }

    const isReviewed = finalizeFinancials.every(
      (item) => item.resolved_by_admin || item.document_type === "NONE"
    );
    if (!isReviewed) {
      alertToast({
        title: "Missing Information",
        message: "Please review all finalize financials",
      });
      return;
    }
    reviewfinalizeFinancialConfirmationModal.open();
  };

  const isFinancialVersionV1 = dayjs(
    closing_card_details.creator.created_at
  ).isAfter(FINANCIAL_CLOSING_V1_DATE);

  return (
    <>
      <Modal.Root open={true} modal={false} onOpenChange={close}>
        <Modal.Content
          size="fullscreen"
          onFocusOutside={(e) => e.preventDefault()}
          asChild
        >
          <FormikForm<ClosingFormValues>
            initialValues={{
              closing_card_details:
                financialClosingDetails?.closing_card_details ||
                ({} as FinancialClosingDetail),
              checklist_items: checkListItems,
              other_financials: other_financials,
              lock_financials: Boolean(
                closing_card_details.accounting_lock_date
              ),
              accounting_lock_date:
                closing_card_details.accounting_lock_date || "",
            }}
            validationSchema={() =>
              financialClosingSchema(checkListItems, other_financials)
            }
            validateOnMount={validateOnMount}
            validateOnChange={false}
            validateOnBlur
            onSubmit={() => {}}
            enableReinitialize
          >
            {({ values, validateForm, submitCount, setFormikState }) => {
              if (submitCount > 0 && !validateOnMount) {
                setValidateOnMount(true);
              }

              const validateChecklist = async () => {
                const errors = await validateForm();
                const hasErrors = Object.keys(errors).length > 0;

                if (hasErrors) {
                  scrollToTheFirstClosingError(errors, values);
                  alertToast({
                    title: "Missing Information",
                    message: "Please complete all required fields",
                  });
                  return false;
                }
                return true;
              };

              const allowUserToChangeState = async () => {
                const isValid = await validateChecklist();
                if (isValid) {
                  reviewfinalizeFinancialConfirmationModal.open();
                }
              };

              const handleSendForReview = async () => {
                const isValid = await validateChecklist();
                if (isValid) {
                  reviewConformationModal.open();
                }
              };

              return (
                <>
                  <div className="t-h-full t-w-full t-overflow-auto">
                    <Modal.Header className="t-sticky t-top-0 t-bg-white t-z-sidebar t-gap-3">
                      <div className="t-flex t-justify-between t-w-full t-items-center">
                        <div>
                          <Modal.Subtitle className="sm:t-hidden">
                            <Breadcrumb.Root>
                              {breadcrumbs.map((e) => (
                                <Breadcrumb.Item link={e.link} key={e.link}>
                                  {e.name}
                                </Breadcrumb.Item>
                              ))}
                            </Breadcrumb.Root>
                          </Modal.Subtitle>
                          <Modal.Title>
                            <div className="t-flex t-items-center t-gap-4 sm:t-flex-col sm:t-items-start sm:t-gap-1">
                              Add financials
                              <span className="t-text-body-sm t-text-text-30">
                                <Async.Root
                                  isLoading={
                                    isUpdating ||
                                    updateFinancialClosingCheckListApiState.isLoading
                                  }
                                  customLoader={
                                    <Loader customType="secondary" />
                                  }
                                  isEmpty={false}
                                  isSuccess={
                                    success ||
                                    updateFinancialClosingCheckListApiState.isSuccess
                                  }
                                >
                                  <Async.Empty>
                                    <></>
                                  </Async.Empty>
                                  <Async.Success>
                                    <span className="t-flex t-items-center t-gap-2 t-transition-all">
                                      <img src={DoubleGreenTick} alt="Saved" />
                                      Changes saved
                                    </span>
                                  </Async.Success>
                                </Async.Root>
                              </span>
                            </div>
                          </Modal.Title>
                        </div>
                        {!isReadOnlyClosing && (
                          <div className="t-ml-auto">
                            <Modal.Root
                              open={checklistModal.isOpen}
                              onOpenChange={checklistModal.toggle}
                            >
                              <Modal.Trigger asChild>
                                <ConditionalToolTip
                                  condition={
                                    closing_card_details?.creator
                                      .is_service_team_user &&
                                    !isCpa &&
                                    "You don't have access to edit the checklist"
                                  }
                                >
                                  <span>
                                    <Button
                                      type="button"
                                      customType={
                                        isMobile ? "icon" : "secondary"
                                      }
                                      aria-label="Manage Checklist"
                                      disabled={
                                        closing_card_details?.creator
                                          .is_service_team_user && !isCpa
                                      }
                                      onClick={checklistModal.open}
                                      size="small"
                                      leftIcon={
                                        isMobile ? undefined : (
                                          <BooksSettingLines />
                                        )
                                      }
                                    >
                                      {isMobile ? (
                                        <BooksSettingLines />
                                      ) : (
                                        "Manage Checklist"
                                      )}
                                    </Button>
                                  </span>
                                </ConditionalToolTip>
                              </Modal.Trigger>
                              <Modal.Content
                                asChild
                                useCustomOverlay
                                size="large"
                              >
                                <ManageClosing onClose={checklistModal.close} />
                              </Modal.Content>
                            </Modal.Root>
                          </div>
                        )}
                      </div>
                      <Modal.RawClose asChild>
                        <Button size="xsmall" customType="icon">
                          <img
                            className="t-h-4 t-w-4"
                            src={CloseIcon}
                            alt="Close Modal"
                          />
                        </Button>
                      </Modal.RawClose>
                    </Modal.Header>
                    <Modal.Body className="t-grid t-grid-cols-[minmax(0,2fr)_minmax(0,1fr)] t-gap-4 sm:t-grid-cols-1 sm:t-px-4">
                      {changeRequests.length > 0 && (
                        <div className="t-col-span-2 sm:t-col-span-1">
                          <ChangeRequestsAccordion
                            changeRequests={changeRequests}
                          />
                        </div>
                      )}
                      <div className="t-flex t-flex-col t-gap-4">
                        <Details
                          disabled={!isEditAllowedForUser}
                          isLoading={
                            isUpdating ||
                            updateFinancialClosingCheckListApiState.isLoading
                          }
                        />

                        <Checklist
                          closingDetails={closing_card_details}
                          isFormEditable={isEditAllowedForUser}
                          currentStatus={closing_card_details.status}
                          otherFinancials={other_financials}
                          isFinancialVersionV1={isFinancialVersionV1}
                        />

                        {(isEditAllowedForUser || !isNotOngoingState) && (
                          <OtherAttachmentSingle
                            other_financials={other_financials}
                            isEditAllowed={isEditAllowedForUser}
                            currentStatus={closing_card_details.status}
                            isCreatedByAdmin={
                              financialClosingDetails?.closing_card_details
                                .creator.is_service_team_user
                            }
                            isFinancialVersionV1={isFinancialVersionV1}
                            year={dayjs(closing_card_details.start_date).year()}
                          />
                        )}

                        <Card.Root className="t-pb-5 t-rounded-lg sm:!t-px-3">
                          <Card.Body>
                            <div className="t-flex t-flex-col t-gap-2">
                              <div className="t-flex t-justify-between sm:t-gap-3">
                                <div>
                                  <Card.Title>Lock Financials</Card.Title>
                                  <p className="t-m-0 t-text-body-sm t-text-text-30">
                                    We'll lock everything up to (and including)
                                    that day to keep your financials clean and
                                    final.
                                  </p>
                                </div>
                                <div className="t-shrink-0">
                                  <SwitchField
                                    size={isMobile ? "small" : "regular"}
                                    disabled={!isEditAllowedForUser}
                                    name="lock_financials"
                                    onChange={(checked) => {
                                      if (checked) {
                                        dateUpdate(
                                          new Date(
                                            closing_card_details.end_date
                                          )
                                        );
                                        return;
                                      }

                                      dateUpdate(null);
                                    }}
                                  />
                                </div>
                              </div>
                              <Field name="accounting_lock_date">
                                {({ field }: FieldProps) => {
                                  return (
                                    <DateInput
                                      {...field}
                                      disabled={
                                        !values.lock_financials ||
                                        !isEditAllowedForUser
                                      }
                                      block
                                      portalId={field.name}
                                      onDateChange={dateUpdate}
                                    />
                                  );
                                }}
                              </Field>
                            </div>
                          </Card.Body>
                        </Card.Root>
                      </div>
                      <div className="t-flex t-flex-col t-gap-4">
                        {summary && (
                          <SummaryCard
                            entityId={entityId}
                            editable={isEditAllowedForUser}
                            summary={summary}
                            financialClosingId={financialClosingId}
                            showSummary={closing_card_details.show_summary}
                          />
                        )}
                        <Comments financialClosingId={financialClosingId} />
                      </div>
                    </Modal.Body>
                  </div>

                  {showResolveOrAmend && (
                    <Modal.Footer className="t-flex t-justify-end t-gap-3">
                      <ChangeRequestActions
                        entityId={entityId}
                        financialClosingId={financialClosingId}
                        onAmended={onAmended}
                      />
                    </Modal.Footer>
                  )}
                  {isReadOnlyClosing && !showResolveOrAmend && (
                    <Modal.Footer className="t-flex t-justify-end">
                      <Button type="button" onClick={close}>
                        Close
                      </Button>
                    </Modal.Footer>
                  )}
                  {showStatusFooter && (
                    <Modal.Footer className="t-flex t-justify-between">
                      <div></div>
                      <div className="t-flex t-gap-6 t-items-center">
                        {isCustomer &&
                          closing_card_details.status ===
                            CLOSING_STATUS.REVIEW_PENDING && (
                            <Button
                              type="button"
                              onClick={() => chatSlider.open()}
                              customType="secondary"
                            >
                              Have doubts? Chat with us
                            </Button>
                          )}
                        <LockedUi
                          feature={WRITE_PERMISSION_KEYS.FINANCIALS}
                          blockedUi={
                            <LockedButton
                              text={
                                ctaTitle === "Ready for review" && isCustomer
                                  ? "Finalise financials"
                                  : ctaTitle
                              }
                            />
                          }
                        >
                          <Button
                            customType="primary"
                            disabled={!isChangeStatusAllowed}
                            onClick={() =>
                              handleFinalizeFinancialStatusChange({
                                allowUserToChangeState,
                                handleSendForReview,
                              })
                            }
                            isLoading={isSendingForReview}
                          >
                            {ctaTitle === "Ready for review" && isCustomer
                              ? "Finalise financials"
                              : ctaTitle}
                          </Button>
                        </LockedUi>
                      </div>
                    </Modal.Footer>
                  )}
                  {chatSlider.isOpen && (
                    <FloatingChat
                      hideOpenedIcon
                      className="!t-z-modal"
                      overlayClassName="!t-z-modal"
                      open={chatSlider.isOpen}
                      onOpenChange={chatSlider.close}
                      defaultChannelId={channelId}
                      filter={groupId ? { group_uuid: { $in: [groupId] } } : {}}
                    />
                  )}
                  <SendForReviewConfrmationModal
                    open={reviewConformationModal.isOpen}
                    onClose={reviewConformationModal.close}
                    onConfirm={onSendReviewConfirmation}
                  />
                  <SendForReviewConfrmationModal
                    open={reopenSendForReviewModal.isOpen}
                    onClose={reopenSendForReviewModal.close}
                    onConfirm={async () => {
                      reopenSendForReviewModal.close();
                      await onChangeStatus(CLOSING_STATUS.REVIEW_PENDING);
                    }}
                  />
                  <FinalizeFinancialConfirmationModal
                    show={reviewfinalizeFinancialConfirmationModal.isOpen}
                    onClose={() =>
                      reviewfinalizeFinancialConfirmationModal.close()
                    }
                    period={
                      financialClosingDetails?.closing_card_details?.period ||
                      ""
                    }
                    onConfirm={() =>
                      onConfirmFinalizeFinancial({
                        state: isCustomer
                          ? CLOSING_STATUS.REVIEW_PENDING
                          : undefined,
                      })
                    }
                  />
                </>
              );
            }}
          </FormikForm>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
